import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, TextFieldProps } from "@mui/material";
import { postRequest } from "../../../api/apiServices";
import apiList from "../../../api/apiList";
import { useDebounce } from "../../../hooks/useDebounce";
import { VonqOption, VonqTaxonomyLink } from "../../../types/vonqMapping";
import { linkToOption, optionToLink } from "./vonqFieldUtils";

export interface VonqJobTitleFieldProps {
  displayValue: string;
  onDisplayChange: (value: string) => void;
  vonqLink?: VonqTaxonomyLink | null;
  onVonqChange: (link: VonqTaxonomyLink | undefined) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  textFieldProps?: Partial<TextFieldProps>;
}

const VonqJobTitleField: React.FC<VonqJobTitleFieldProps> = ({
  displayValue,
  onDisplayChange,
  vonqLink,
  onVonqChange,
  placeholder = "Search and select a job title from the list",
  error,
  helperText,
  textFieldProps,
}) => {
  const [options, setOptions] = useState<VonqOption[]>([]);
  const [selected, setSelected] = useState<VonqOption | null>(
    linkToOption(vonqLink)
  );
  const [inputValue, setInputValue] = useState(
    vonqLink?.description || displayValue || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearch = useDebounce(inputValue, 500);

  useEffect(() => {
    const option = linkToOption(vonqLink);
    if (option) {
      setSelected(option);
      setInputValue(option.name);
      return;
    }

    if (displayValue) {
      setInputValue(displayValue);
    }
  }, [vonqLink?.vonqId, vonqLink?.description, displayValue]);

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setOptions(selected ? [selected] : []);
      return;
    }

    setIsLoading(true);
    postRequest(apiList.getJobTitle, { search: debouncedSearch })
      .then((response) => {
        const items: VonqOption[] = response?.data?.data || [];
        if (selected && !items.some((item) => item.id === selected.id)) {
          setOptions([selected, ...items]);
        } else {
          setOptions(items);
        }
      })
      .catch(() => setOptions(selected ? [selected] : []))
      .finally(() => setIsLoading(false));
  }, [debouncedSearch, selected]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      inputValue={inputValue}
      loading={isLoading}
      onInputChange={(_, newInput, reason) => {
        if (reason === "reset") return;
        setInputValue(newInput);
        if (reason === "clear") {
          setSelected(null);
          onVonqChange(undefined);
          onDisplayChange("");
          return;
        }
        if (reason === "input" && selected && newInput !== selected.name) {
          setSelected(null);
          onVonqChange(undefined);
          onDisplayChange("");
        }
      }}
      onChange={(_, option) => {
        if (!option) {
          setSelected(null);
          onVonqChange(undefined);
          onDisplayChange("");
          setInputValue("");
          return;
        }
        setSelected(option);
        const link = optionToLink(option);
        onVonqChange(link);
        onDisplayChange(option.name);
        setInputValue(option.name);
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={
        isLoading
          ? "Loading..."
          : inputValue.trim()
            ? "No job titles found"
            : "Type to search job titles"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          className={textFieldProps?.className || "input-box-custom"}
        />
      )}
    />
  );
};

export default VonqJobTitleField;
